import { useState, useEffect, useRef, useMemo } from "react";
import type { Folder } from "./folders";

const MAX_PREVIEW_PAGES = 3;

type FilterType = "all" | "undefined" | "defined";

interface PDFFile {
  name: string;
  path: string;
  etag: string;
  pageCount: number;
  lastModified: string;
}

// Parse filename into date prefix and name part (without extension)
function parseFileName(filename: string): {
  datePrefix: string | null;
  namePart: string;
} {
  const dateMatch = filename.match(/^(\d{4}-\d{2}-\d{2})-/);
  const withoutDate = dateMatch
    ? filename.slice(dateMatch[0].length)
    : filename;
  const dotIndex = withoutDate.lastIndexOf(".");
  const namePart = dotIndex > 0 ? withoutDate.slice(0, dotIndex) : withoutDate;
  return {
    datePrefix: dateMatch ? dateMatch[1] : null,
    namePart,
  };
}

function Lightbox({
  etag,
  pageCount,
  startPage,
  onClose,
}: {
  etag: string;
  pageCount: number;
  startPage: number;
  onClose: () => void;
}) {
  const [currentPage, setCurrentPage] = useState(startPage);
  const [touchStart, setTouchStart] = useState<number | null>(null);

  const goNext = () => setCurrentPage((p) => Math.min(p + 1, pageCount));
  const goPrev = () => setCurrentPage((p) => Math.max(p - 1, 1));

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" || e.key === " ") goNext();
      else if (e.key === "ArrowLeft") goPrev();
    };
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [onClose, pageCount]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goNext();
      else goPrev();
    }
    setTouchStart(null);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center"
      onClick={onClose}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <img
        src={`/api/preview/${etag}/${currentPage}`}
        alt={`Page ${currentPage}`}
        className="max-w-full max-h-[calc(100%-3rem)] object-contain p-4"
        onClick={(e) => e.stopPropagation()}
      />
      <div className="flex items-center gap-4 text-white text-sm py-2">
        <button
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded bg-white/20 disabled:opacity-30"
        >
          ←
        </button>
        <span>{currentPage} / {pageCount}</span>
        <button
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          disabled={currentPage === pageCount}
          className="px-3 py-1 rounded bg-white/20 disabled:opacity-30"
        >
          →
        </button>
      </div>
    </div>
  );
}

function LazyImage({
  src,
  alt,
  className,
  onClick,
}: {
  src: string;
  alt: string;
  className?: string;
  onClick?: () => void;
}) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "100px" }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={imgRef}
      className={`bg-gray-100 ${onClick ? "cursor-pointer" : ""} ${className}`}
      onClick={onClick}
    >
      {isVisible && (
        <img
          src={src}
          alt={alt}
          className={`w-full h-full object-contain transition-opacity duration-300 ${
            isLoaded ? "opacity-100" : "opacity-0"
          }`}
          onLoad={() => setIsLoaded(true)}
        />
      )}
      {(!isVisible || !isLoaded) && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
}

// Extract the name part from a filename (without date prefix and extension)
function extractNamePart(filename: string): string {
  const dateMatch = filename.match(/^\d{4}-\d{2}-\d{2}-/);
  const withoutDate = dateMatch
    ? filename.slice(dateMatch[0].length)
    : filename;
  const dotIndex = withoutDate.lastIndexOf(".");
  return dotIndex > 0 ? withoutDate.slice(0, dotIndex) : withoutDate;
}

function PDFCard({
  file,
  onRename,
  onPageCountUpdate,
  allFiles,
}: {
  file: PDFFile;
  onRename: (oldPath: string, newName: string) => Promise<void>;
  onPageCountUpdate: (path: string, pageCount: number) => void;
  allFiles: PDFFile[];
}) {
  const previewPages = Math.min(file.pageCount, MAX_PREVIEW_PAGES);
  const hasMorePages = file.pageCount > MAX_PREVIEW_PAGES;
  const [lightboxPage, setLightboxPage] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(() => file.name.replace(/\.pdf$/i, ""));
  const [isRenaming, setIsRenaming] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Trigger preview generation when card is visible and pageCount is 0
  useEffect(() => {
    if (file.pageCount > 0 || isGenerating) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          setIsGenerating(true);

          fetch("/api/preview/generate", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: file.name,
              path: file.path,
              etag: file.etag,
              lastModified: file.lastModified,
            }),
          })
            .then((res) => res.json())
            .then((data) => {
              if (data.pageCount) {
                onPageCountUpdate(file.path, data.pageCount);
              }
            })
            .catch((err) => console.error("Preview generation failed:", err))
            .finally(() => setIsGenerating(false));
        }
      },
      { rootMargin: "100px" }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, [file.pageCount, file.path, file.etag, file.name, file.lastModified, isGenerating, onPageCountUpdate]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });

      // Check if filename starts with date format YYYY-MM-DD-
      const dateMatch = editName.match(/^\d{4}-\d{2}-\d{2}-/);
      const startIndex = dateMatch ? dateMatch[0].length : 0;

      inputRef.current.setSelectionRange(startIndex, editName.length);
    }
  }, [isEditing]);

  // Get unique name parts from all files for suggestions
  const allNameParts = useMemo(() => {
    const parts = new Set<string>();
    for (const f of allFiles) {
      if (f.path !== file.path) {
        const namePart = extractNamePart(f.name);
        if (namePart) parts.add(namePart);
      }
    }
    return Array.from(parts);
  }, [allFiles, file.path]);

  // Get current name part being edited and filter suggestions
  const suggestions = useMemo(() => {
    if (!isEditing) return [];

    const dateMatch = editName.match(/^\d{4}-\d{2}-\d{2}-/);
    if (!dateMatch) return [];

    const currentNamePart = extractNamePart(editName).toLowerCase();
    if (!currentNamePart) return [];

    return allNameParts
      .filter(
        (name) =>
          name.toLowerCase().includes(currentNamePart) &&
          name.toLowerCase() !== currentNamePart
      )
      .slice(0, 5);
  }, [isEditing, editName, allNameParts]);

  const applySuggestion = (suggestion: string) => {
    const dateMatch = editName.match(/^\d{4}-\d{2}-\d{2}-/);
    const datePrefix = dateMatch ? dateMatch[0] : "";
    setEditName(`${datePrefix}${suggestion}`);
    inputRef.current?.focus();
  };

  const handleRename = async () => {
    const trimmedName = editName.trim();
    const fullName = trimmedName + ".pdf";
    if (!trimmedName || fullName === file.name) {
      setIsEditing(false);
      setRenameError(null);
      return;
    }

    setIsRenaming(true);
    setRenameError(null);
    try {
      await onRename(file.path, fullName);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
      setIsEditing(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Rename failed";
      if (message.includes("412") || message.includes("Precondition")) {
        setRenameError("File already exists");
      } else {
        setRenameError(message);
      }
    } finally {
      setIsRenaming(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleRename();
    } else if (e.key === "Escape") {
      setIsEditing(false);
    }
  };

  return (
    <>
      {lightboxPage !== null && (
        <Lightbox
          etag={file.etag}
          pageCount={file.pageCount}
          startPage={lightboxPage}
          onClose={() => setLightboxPage(null)}
        />
      )}
      <div ref={cardRef} className="relative bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
        <a
          href={`/api/nextcloud/download?path=${encodeURIComponent(file.path)}`}
          target="_blank"
          rel="noopener noreferrer"
          title="Open PDF"
          aria-label={`Open ${file.name}`}
          onClick={(e) => e.stopPropagation()}
          className="absolute top-2 right-2 z-10 inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm text-gray-700 border border-gray-200 shadow-md hover:bg-blue-600 hover:text-white hover:border-blue-600 hover:shadow-lg active:scale-95 transition-all"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
            />
          </svg>
        </a>
        {previewPages > 0 ? (
          <div className="flex gap-1 p-2 bg-gray-50 overflow-x-auto">
            {Array.from({ length: previewPages }, (_, i) => (
              <LazyImage
                key={i}
                src={`/api/preview/${file.etag}/${i + 1}`}
                alt={`${file.name} page ${i + 1}`}
                className="relative shrink-0 w-43 h-60 sm:w-43 sm:h-66 rounded border border-gray-200"
                onClick={() => setLightboxPage(i + 1)}
              />
            ))}
            {hasMorePages && (
              <div
                className="shrink-0 w-43 h-60 sm:w-43 sm:h-66 rounded border border-gray-200 bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors"
                onClick={() => setLightboxPage(MAX_PREVIEW_PAGES + 1)}
              >
                <span className="text-gray-500 text-sm font-medium">
                  +{file.pageCount - MAX_PREVIEW_PAGES}
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="h-60 sm:h-66 bg-gray-50 flex items-center justify-center">
            {isGenerating ? (
              <div className="flex flex-col items-center gap-2">
                <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                <span className="text-gray-400 text-sm">Generating...</span>
              </div>
            ) : (
              <span className="text-gray-400 text-sm">No preview</span>
            )}
          </div>
        )}
        <div className="p-3 border-t border-gray-100">
          {isEditing ? (
            <div>
              <textarea
                ref={inputRef}
                value={editName}
                onChange={(e) => {
                  setEditName(e.target.value);
                  setRenameError(null);
                }}
                onKeyDown={handleKeyDown}
                onBlur={handleRename}
                disabled={isRenaming}
                rows={2}
                className={`w-full text-base font-medium text-gray-900 px-2 py-1 border rounded outline-none focus:ring-2 disabled:opacity-50 resize-none break-all ${
                  renameError
                    ? "border-red-500 focus:ring-red-500"
                    : "border-blue-500 focus:ring-blue-500"
                }`}
              />
              {renameError && (
                <p className="text-xs text-red-600 mt-1">{renameError}</p>
              )}
            </div>
          ) : (
            <div
              className="relative cursor-pointer hover:text-blue-600"
              title={file.name}
              onClick={() => {
                setEditName(file.name.replace(/\.pdf$/i, ""));
                setRenameError(null);
                setIsEditing(true);
              }}
            >
              {(() => {
                const { datePrefix, namePart } = parseFileName(file.name);
                return (
                  <>
                    {datePrefix && (
                      <p className="text-base text-gray-500">{datePrefix}</p>
                    )}
                    <p className="text-base font-medium text-gray-900">
                      {namePart}
                    </p>
                  </>
                );
              })()}
              {showSuccess && (
                <span className="absolute right-0 top-1/2 -translate-y-1/2 text-green-600 animate-pulse">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </span>
              )}
            </div>
          )}

          {isEditing && suggestions.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    applySuggestion(suggestion);
                  }}
                  className="px-2 py-1 text-xs bg-white hover:bg-blue-50 text-gray-700 hover:text-blue-700 rounded border border-gray-200 hover:border-blue-300 transition-colors shadow-sm"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}

          <p className="text-xs text-gray-500 mt-1">
            {file.pageCount} {file.pageCount === 1 ? "page" : "pages"} ·{" "}
            {new Date(file.lastModified).toLocaleString()}
          </p>
        </div>
      </div>
    </>
  );
}

export function PDFBrowser() {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(true);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [files, setFiles] = useState<PDFFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch folders on mount
  useEffect(() => {
    fetch("/api/folders")
      .then((res) => res.json())
      .then((data) => {
        setFolders(data);
        // Set initial folder from hash
        const hash = window.location.hash.slice(1);
        const folder = data.find((f: Folder) => f.id === hash);
        if (folder) {
          setSelectedFolder(folder.id);
        }
      })
      .catch((err) => {
        console.error("Failed to load folders:", err);
      })
      .finally(() => setFoldersLoading(false));
  }, []);

  // Sort folders to put selected one first
  const sortedFolders = useMemo(() => {
    if (!selectedFolder) return folders;
    return [...folders].sort((a, b) => {
      if (a.id === selectedFolder) return -1;
      if (b.id === selectedFolder) return 1;
      return 0;
    });
  }, [folders, selectedFolder]);


  const selectFolder = (folderId: string) => {
    setSelectedFolder(folderId);
    window.location.hash = folderId;
  };

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      const folder = folders.find((f) => f.id === hash);
      if (folder) {
        setSelectedFolder(folder.id);
      }
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [folders]);

  useEffect(() => {
    if (!selectedFolder) return;

    const folder = folders.find((f) => f.id === selectedFolder);
    if (!folder) return;

    setLoading(true);
    setError(null);

    const params = new URLSearchParams({
      path: folder.path,
      filter,
    });
    if (debouncedSearch.trim()) {
      params.set("search", debouncedSearch.trim());
    }

    fetch(`/api/nextcloud/files?${params}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          setError(data.error);
          setFiles([]);
        } else {
          setFiles(data.files);
        }
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Failed to load files");
        setFiles([]);
      })
      .finally(() => setLoading(false));
  }, [selectedFolder, folders, filter, debouncedSearch]);

  const handleRenameFile = async (oldPath: string, newName: string) => {
    const response = await fetch("/api/nextcloud/rename", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ oldPath, newName }),
    });
    const data = await response.json();
    if (data.error) {
      throw new Error(data.error);
    }
    // Update the file in the list with the new name and path
    setFiles((prev) =>
      prev.map((f) =>
        f.path === oldPath ? { ...f, name: newName, path: data.newPath } : f
      )
    );
  };

  const handlePageCountUpdate = (path: string, pageCount: number) => {
    setFiles((prev) =>
      prev.map((f) => (f.path === path ? { ...f, pageCount } : f))
    );
  };

  // Show empty page while folders are loading
  if (foldersLoading) {
    return <div className="w-full max-w-3xl mx-auto px-4 py-6 sm:py-8" />;
  }

  return (
    <div className="w-full max-w-3xl mx-auto px-4 py-6 sm:py-8">
      <div className="flex gap-1 mb-4 items-center flex-wrap">
        <div className="flex gap-1 overflow-x-auto overflow-y-hidden shrink-0 max-w-full sm:max-w-none sm:overflow-visible">
          {sortedFolders.map((folder) => (
            <button
              key={folder.id}
              onClick={() => selectFolder(folder.id)}
              className={`px-4 py-2 text-white rounded-lg font-medium transition-opacity whitespace-nowrap ${
                selectedFolder === folder.id ? "opacity-100" : "opacity-50 hover:opacity-80"
              }`}
              style={ {backgroundColor: folder.color}}
            >
              {folder.id}
            </button>
          ))}
        </div>
        <div className="ml-auto flex flex-1 gap-2 items-center">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Suchen..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 text-base w-40 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as FilterType)}
            className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 text-base"
          >
            <option value="all">Alle</option>
            <option value="undefined">Offen</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="p-3 mb-4 bg-red-50 text-red-700 border border-red-200 rounded-lg">
          {error}
        </div>
      )}

      {files.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {files.map((file) => (
            <PDFCard
              key={file.path}
              file={file}
              onRename={handleRenameFile}
              onPageCountUpdate={handlePageCountUpdate}
              allFiles={files}
            />
          ))}
        </div>
      )}

      {files.length === 0 && !loading && !error && selectedFolder && (
        <p className="text-gray-500">No PDF files found.</p>
      )}
    </div>
  );
}
